import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import Select from "react-select";
import { UserContext } from "../context/UserContext";
import { useGroupsFilter } from "../context/GroupsFilterContext";

type YearOption = { value: number; label: string };

function Navbar() {
    const { t } = useTranslation();
    const navigate = useHistory();
    const { logOff } = useContext(UserContext);
    const { years, selectedYear, setSelectedYear, groups, error, loading } = useGroupsFilter();

    const yearOptions: YearOption[] = useMemo(
        () => years.map(y => ({ value: y, label: String(y) })),
        [years]
    );
    const currentOption = useMemo(
        () => yearOptions.find(o => o.value === selectedYear) ?? null,
        [yearOptions, selectedYear]
    );

    const errorMessage = error
        ? error.startsWith('error_status:')
            ? t('nav.error_status', { status: error.split(':')[1] })
            : t('nav.connection_error')
        : null;

    return (
        <div
            className="vh-100 bg-white border-end shadow-sm d-none d-lg-flex flex-column"
            style={{ width: "260px", padding: "1.5rem" }}
        >
            <div className="mb-3">
                <span className="fs-4 fw-bold text-primary">{t('nav.groups')}</span>
            </div>

            <div className="mb-2">
                <Select<YearOption>
                    classNamePrefix="year-select"
                    options={yearOptions}
                    value={currentOption}
                    onChange={(opt) => opt && setSelectedYear(opt.value)}
                    placeholder={t('nav.year_placeholder')}
                    isSearchable={false}
                    menuPlacement="auto"
                    styles={{
                        control: (base) => ({ ...base, borderColor: '#dee2e6', boxShadow: 'none', minHeight: '38px' }),
                        singleValue: (base) => ({ ...base, color: '#0d6efd', fontWeight: 600 }),
                    }}
                />
            </div>

            <hr className="mt-0" />

            {errorMessage && (
                <div className="alert alert-danger p-2 small">{errorMessage}</div>
            )}

            <div className="flex-grow-1" style={{ overflowY: 'auto' }}>
                {loading && groups.length === 0 && (
                    <div className="text-muted small px-2">{t('nav.loading')}</div>
                )}
                {!loading && groups.length === 0 && !errorMessage && (
                    <div className="text-muted small px-2">{t('nav.empty')}</div>
                )}
                {groups.map((el: any) => (
                    <div
                        key={el.id}
                        className="py-2 px-2 mb-1 border-bottom"
                        style={{ cursor: "pointer", transition: "background 0.2s" }}
                        onClick={() => navigate.push(`/group?id=${el.id}&name=${el.name}`)}
                        onMouseOver={(e) => (e.currentTarget.style.background = "#f0f8ff")}
                        onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                        <span className="text-primary fw-semibold">{el.name}</span>
                    </div>
                ))}
            </div>

            <div className="mt-4">
                <button className="btn btn-link text-danger p-0" onClick={logOff}>
                    {t('nav.logout')}
                </button>
            </div>
        </div>
    );
}

export default Navbar;
